import React, { useState } from 'react';
import { useParams } from 'react-router';
import { Button, Alert, Box, Grid, Card, CardMedia, CardContent, Typography, IconButton, Stack } from '@mui/material';
import useApi from '../../hooks/useApi';
import useSnackbar from '../../hooks/useSnackbar';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCustomColourModal from './modal/AddCustomColourModal';

const hexToRgb = (hex) => {
    hex = hex.replace(/^#/, '');
    const bigint = parseInt(hex, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return { r, g, b };
};

const CustomColour = ({ account, onRefresh }) => {
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState(false);
    const { handlePost, handleDelete } = useApi();
    const [customColourDialog, setCustomColourDialog] = useState(false);
    const { showSnackbar } = useSnackbar();

    const handleOnSubmit = async (colour, label) => {
        setIsLoading(true);

        const uri = `/superadmin/accounts/${id}/colour`;
        const response = await handlePost(uri, { value: colour, label: label });

        setIsLoading(false);

        if (!response.ok) {
            showSnackbar('An error occurred.', 'error');
        }
        else {
            showSnackbar('Colour added successfully.', 'success');
            setCustomColourDialog(false);
        }

        onRefresh();
    };

    const handleDeleteColour = async (row) => {
        setIsLoading(true);

        const uri = `/superadmin/accounts/${id}/colour/${row.id}`;
        const response = await handleDelete(uri);

        setIsLoading(false);

        if (!response.ok) {
            showSnackbar('An error occurred.', 'error');
        }
        else {
            showSnackbar('Colour deleted successfully.', 'success');
            setCustomColourDialog(false);
        }

        onRefresh();
    };

    return (
        <>
            <Box>
                <Button variant="contained" onClick={() => setCustomColourDialog(true)}>Add Brand Colour</Button>
                {account.customColours.length > 0 ? (
                    <Box sx={{ flexGrow: 1, mt: 2, width: '100%' }}>
                        <Grid
                            container
                            spacing={2}
                        >
                            {account.customColours.map((colour, index) => {
                                const rgb = hexToRgb(colour.value);
                                return (
                                    <Grid
                                        size={{ xs: 12, sm: 6, md: 3 }}
                                        key={colour.id ?? index}
                                    >
                                        <Card
                                            sx={{
                                                borderRadius: 5,
                                                display: 'flex',
                                                flexDirection: 'column'
                                            }}
                                        >
                                            <CardMedia
                                                sx={{
                                                    backgroundColor: colour.value,
                                                    borderTopLeftRadius: 8,
                                                    borderTopRightRadius: 8,
                                                    aspectRatio: '16 / 9'
                                                }}
                                            />

                                            <CardContent sx={{ pt: 1 }}>
                                                <Stack direction="row" alignItems="start" spacing={1}>
                                                    <Typography
                                                        variant="h6"
                                                        title={colour.label ?? ''}
                                                        sx={{
                                                            flexGrow: 1,
                                                            wordBreak: 'break-word'
                                                        }}
                                                    >
                                                        {colour.label ?? ''}
                                                    </Typography>

                                                    <IconButton
                                                        onClick={() => handleDeleteColour(colour)}
                                                        size="small"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Stack>

                                                <Stack spacing={0.2}>
                                                    <Typography variant="subtitle2" color="text.secondary">
                                                        HEX: {colour.value}
                                                    </Typography>
                                                    <Typography variant="subtitle2" color="text.secondary">
                                                        RGB: ({rgb.r}, {rgb.g}, {rgb.b})
                                                    </Typography>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Box>
                ) : (
                    <Alert sx={{ mt: 2 }} severity="info">No brand colours available.</Alert>
                )}
            </Box>
            {customColourDialog && (
                <AddCustomColourModal
                    shown={customColourDialog}
                    onHide={() => setCustomColourDialog(false)}
                    onSubmit={handleOnSubmit}
                    onLoading={isLoading}
                />
            )}
        </>
    );
};

export default CustomColour;